import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, ArrowRight } from 'lucide-react';
import { fetchAllNewsPosts } from '../lib/api';

export default function NewsIndex() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadPosts() {
      const data = await fetchAllNewsPosts();
      setPosts(data);
      setLoading(false);
    }
    loadPosts();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-sky-500"></div>
      </div>
    );
  }

  return (
    <div className="bg-white text-slate-600 min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[45vh] min-h-[300px] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat scale-105"
          style={{ 
            backgroundImage: `url('https://assets.auxodev.dk/assets/814e446f-d222-4d23-8be7-5a6a25bb15ff/6ccf7eb1-0ddd-4c73-aab3-801631a74b5c.jpg')` 
          }}
        />
        <div className="absolute inset-0 bg-black/45" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 text-center w-full space-y-6">
          <h1 className="text-xl sm:text-3xl lg:text-5xl font-bold text-white tracking-wider break-words px-4">
            Seneste oplæg
          </h1>
          <p className="text-[28px] text-slate-200 font-light tracking-wide">
            Se vores projekter her.
          </p>
          <div className="flex flex-wrap justify-center gap-4 pt-2">
            <Link 
              to="/kontakt" 
              className="bg-[#0073A5] hover:bg-[#005c84] text-white px-8 py-3.5 text-xs font-bold tracking-widest uppercase transition-colors duration-200"
            >
              Kontakt os
            </Link>
            <Link 
              to="/referencer" 
              className="bg-[#0073A5] hover:bg-[#005c84] text-white px-8 py-3.5 text-xs font-bold tracking-widest uppercase transition-colors duration-200"
            >
              Læs mere
            </Link>
          </div>
        </div>
      </section>

      {/* News Grid Section */}
      <section className="py-16 sm:py-24 max-w-7xl mx-auto px-6 lg:px-8">
        <div className="space-y-12">


          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 pt-6">
            {posts.map((post) => (
              <article 
                key={post.id} 
                className="bg-slate-50 border border-slate-200 overflow-hidden flex flex-col group hover:border-sky-500 transition-all duration-300"
              >
                {/* Image */}
                <Link to={`/nyheder/${post.slug}`} className="aspect-video bg-slate-100 overflow-hidden block">
                  <img 
                    src={post.images?.[0] || 'https://assets.auxodev.dk/assets/814e446f-d222-4d23-8be7-5a6a25bb15ff/55dfd408-b442-4dfe-be75-a310b44063dd.jpg'} 
                    alt={post.title} 
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                  />
                </Link>

                {/* Content */}
                <div className="p-6 flex-1 flex flex-col justify-between space-y-4">
                  <div className="space-y-3">
                    {/* Date */}
                    <div className="flex items-center gap-1.5 text-xs text-slate-500 font-medium">
                      <Calendar className="w-3.5 h-3.5 text-sky-500" />
                      <span>{post.date}</span>
                    </div>
                    {/* Title */}
                    <h3 className="text-lg font-bold text-slate-900 uppercase tracking-wide group-hover:text-sky-600 transition-colors line-clamp-2">
                      <Link to={`/nyheder/${post.slug}`}>{post.title}</Link>
                    </h3>
                    {/* Summary */}
                    <p className="text-base text-slate-500 font-light leading-relaxed line-clamp-3">
                      {post.summary}
                    </p>
                  </div>

                  {/* Link */}
                  <div className="pt-4 border-t border-slate-200">
                    <Link 
                      to={`/nyheder/${post.slug}`} 
                      className="text-sky-600 hover:text-sky-700 text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 group"
                    >
                      Se mere <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}